import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, estadoDe } from '@/lib/api';

/**
 * api.ts — la inducción del personal, `/api/admin/induction`. Módulo 11,
 * opción 5.
 *
 * Las tres llamadas del vanilla, con lo que el servidor añadió el 2026-09-22
 * para que la pantalla dejara de adivinar:
 *
 * - `roles`: el filtro de rol ofrecía tres escritos a mano y en la base hay
 *   cuatro. Ahora salen de la tabla de roles, con su nombre.
 * - `scoped_area`: el área a la que está acotado quien consulta. La pantalla
 *   ofrecía todas y el servidor ignoraba la elegida sin decirlo.
 * - `enrolled` en cada curso: «sin inscribir» y «0 %» eran el mismo círculo.
 * - El recordatorio masivo acepta los filtros de la tabla: escribe a quien se
 *   ve, no a toda la empresa.
 *
 * Qué cursos cuentan —los obligatorios **publicados**— y cómo se promedia el
 * avance lo decide `app/services/induccion_service.py`.
 */

export type EstadoDeInduccion = 'al_dia' | 'pendiente' | 'sin_iniciar';

export interface CursoDeLaPersona {
  course_id: number;
  title: string;
  enrolled: boolean;
  progress_pct: number;
  completed: boolean;
  has_cert: boolean;
}

export interface PersonaEnInduccion {
  user_id: number;
  full_name: string;
  email: string;
  role: string;
  area_id: number | null;
  area_name: string | null;
  /** Una columna DATE: `yyyy-mm-dd`, sin hora. */
  hire_date: string | null;
  enrolled_count: number;
  total_mandatory: number;
  completed: number;
  pending: number;
  /** El promedio sobre TODOS los obligatorios: un curso sin inscribir cuenta 0. */
  avg_progress: number;
  status: EstadoDeInduccion;
  courses: CursoDeLaPersona[];
}

export interface Induccion {
  /** Contado antes del filtro de estado: sus cifras no cambian al pulsar una. */
  summary: Record<EstadoDeInduccion, number> & { total: number };
  total_mandatory: number;
  mandatory_courses: Array<{ id: number; title: string }>;
  items: PersonaEnInduccion[];
  roles: Array<{ key: string; name: string }>;
  scoped_area: { id: number; name: string | null } | null;
}

export interface FiltrosDeInduccion {
  status: EstadoDeInduccion | '';
  role: string;
  area_id: string;
  search: string;
}

export const FILTROS_VACIOS: FiltrosDeInduccion = { status: '', role: '', area_id: '', search: '' };

export const clavesDeInduccion = {
  todo: ['formacion', 'induccion'] as const,
  lista: (f: FiltrosDeInduccion) => ['formacion', 'induccion', 'lista', f] as const,
};

/** Solo los filtros puestos. El servidor lee un `area_id` vacío como un entero
 *  que no es, y un `status` vacío filtraría a nadie. */
function parametros(f: FiltrosDeInduccion) {
  const p: Record<string, string | number> = {};
  if (f.status) p.status = f.status;
  if (f.role) p.role = f.role;
  if (f.area_id) p.area_id = Number(f.area_id);
  if (f.search.trim()) p.search = f.search.trim();
  return p;
}

export function useInduccion(filtros: FiltrosDeInduccion) {
  return useQuery({
    queryKey: clavesDeInduccion.lista(filtros),
    queryFn: () => api.get<Induccion>('/admin/induction', parametros(filtros)),
    // Al cambiar un filtro, la tabla anterior se queda mientras llega la nueva
    // en vez de volver al esqueleto en cada tecla.
    placeholderData: keepPreviousData,
  });
}

export interface RespuestaDeRecordatorio {
  message: string;
  sent: boolean | number;
}

/** Si el servidor dice que no había nada que recordar —la persona terminó
 *  mientras tanto— o que ya no existe, la fila está vieja: se vuelve a pedir la
 *  lista. Un fallo de red no, porque cambiaría la tabla por un estado de error
 *  por un correo que no salió. */
export function useRecordar() {
  const qc = useQueryClient();
  const refrescar = () => qc.invalidateQueries({ queryKey: clavesDeInduccion.todo });
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<RespuestaDeRecordatorio>(`/admin/induction/${userId}/remind`, null),
    onSuccess: (r) => { if (!r.sent) void refrescar(); },
    onError: (err) => { if (estadoDe(err) === 404) void refrescar(); },
  });
}

/** El masivo lleva los filtros de la tabla: el servidor vuelve a calcular a
 *  quién le falta, con el alcance de quien envía, y no se fía de una lista de
 *  ids que ya podría estar vieja. */
export function useRecordarATodos() {
  return useMutation({
    mutationFn: (filtros: FiltrosDeInduccion) =>
      api.post<RespuestaDeRecordatorio>('/admin/induction/remind-pending', parametros(filtros)),
  });
}
